// Catalog.ID Client — The `cid/bitcash-wallets` document
// ============================================================================
// The member's PERSONAL bitcash wallets, as one document in the account's
// encrypted blob store (cid-encrypted-storage §7.2). It replaces the
// `bitcash_wallet@n` key-bag entries the account used to carry: D5 is relaxed
// (2026-09-17) and a personal wallet key is now PIN-level like every other
// device-storable secret, so the list lives under a key derived from the
// account's `derive_root` and is readable on any device the member has
// unlocked — no supersecret, no bag write, no forced re-unlock of the
// account's other devices.
//
// The ORGANIZATION's wallet did NOT move: an org has no usable derive root for
// its delegates (§5.1), so it stays an entitled org key wrapped for owner and
// treasurer, encoded by `crypto/bitcash-wallet-entry.ts`.
//
// ── what `slot` is ─────────────────────────────────────────────────────────
// Exactly the former `keyIndex`: 0 is the registration wallet, an import or a
// later mint takes max+1, and an abandoned wallet is marked `revoked` rather
// than removed so a slot is NEVER reused. That is not cosmetic — the
// grant-policy id is `bitcash_wallet#<slot>` and the device token-cache slot is
// named from it, so reusing a slot would silently hand a new wallet the
// standing grants and the cached spending token of the abandoned one.
//
// ── why the decoder re-checks everything ───────────────────────────────────
// This module is the only gate between a blob that any node in the federation
// may have replicated and a UI that will offer to SPEND what it names. The bag
// entry it replaces took the same posture for the same reason; nothing about
// moving to a blob makes the bytes more trustworthy.
//
// DOM-free and side-effect-free: the reads and writes live in
// `state/bitcash-wallets-store.ts`, the drawing in
// `pane-types/bitcash-wallets-group.ts`.

import {
    BITCASH_WALLET_LABEL_MAX, type BitcashWalletSource,
} from './bitcash-wallet-entry';
import {
    TYPE_CASH, extractType, generateWalletKeyFrom, toCompact, verifyWalletKey,
} from './bitcash';

/** The blob's name inside the vault's reserved `cid` namespace. LOAD-BEARING:
 *  `deriveBlobKey(root, 'cid', name)` mixes the name into the HKDF info, so
 *  changing it makes every stored document unopenable. */
export const BITCASH_WALLETS_BLOB = 'bitcash-wallets';

/** The only version that exists. Pre-live, so there is nothing to migrate from
 *  — a document that is not v1 is refused rather than upgraded. */
export const BITCASH_WALLETS_DOCUMENT_VERSION = 1;

/** A wallet's lifecycle. `revoked` keeps the slot occupied and the record
 *  readable: the bitcash manager must know to retire the wallet, which it
 *  cannot learn from an absence. */
export type BitcashWalletStatus = 'active' | 'revoked';

const STATUSES: readonly BitcashWalletStatus[] = ['active', 'revoked'];
const SOURCES: readonly BitcashWalletSource[] = ['registration', 'import', 'settings'];

/** One wallet. Field names and types ARE the wire contract: the document is
 *  serialized as RFC 8785 canonical JSON before encryption, and the bitcash
 *  module reads the same shape out of a `bitcash-wallets-release` answer. */
export interface BitcashWalletRecord {
    /** The wallet slot, 0-based. Never reused — see the module header. */
    slot: number;
    /** The 24-character COMPACT spending key. A bearer secret: it goes to the
     *  bitcash Worker and nowhere else. There is no operator field: a code
     *  carries no operator (docs/cid-governance.md M13; Bitcash Ledger
     *  Specification v2 §2.4), so nothing is stored or checked beside it. */
    key: string;
    /** What the member calls this wallet. May be empty. */
    label: string;
    /** Unix SECONDS. */
    createdAt: number;
    source: BitcashWalletSource;
    status: BitcashWalletStatus;
}

export interface BitcashWalletsDocument {
    v: 1;
    wallets: BitcashWalletRecord[];
}

/** A record that has not been given a slot yet — what the planners produce and
 *  {@link appendWallet} files. */
export type NewWalletRecord = Omit<BitcashWalletRecord, 'slot'>;

/**
 * One wallet as a pane or the Key Grant consent picker renders it.
 *
 * `keyIndex` is the record's `slot` under its former name, deliberately: the
 * two panes, the wallet card and `policyKeyIdForChoice` all render it, and
 * renaming the field would have silently changed the grant-policy ids those
 * derive. Re-exported from `pane-types/bitcash-wallets-core.ts`, which is
 * where every consumer already imports it from.
 */
export interface WalletSlot {
    keyIndex: number;
    key: string;
    label: string;
    source: BitcashWalletSource;
    createdAt: number;
}

// ── reading ─────────────────────────────────────────────────────────────────

/** The document an account with no wallets has. Written at generation 0. */
export function emptyWalletsDocument(): BitcashWalletsDocument {
    return { v: 1, wallets: [] };
}

/**
 * Validate a decrypted blob and return the canonical shape (keys folded to
 * compact form). Throws on anything the format does not promise — see the
 * module header for why this is strict rather than lenient.
 */
export function decodeWalletsDocument(value: unknown): BitcashWalletsDocument {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        throw new Error('Your bitcash wallet list is unreadable');
    }
    const o = value as Record<string, unknown>;
    if (o.v !== BITCASH_WALLETS_DOCUMENT_VERSION) {
        throw new Error(`Unsupported bitcash wallet document version: ${String(o.v)}`);
    }
    if (!Array.isArray(o.wallets)) {
        throw new Error('The bitcash wallet document does not hold a wallet list');
    }

    const seen = new Set<number>();
    const wallets = (o.wallets as unknown[]).map((raw) => {
        const wallet = validateRecord(raw);
        if (seen.has(wallet.slot)) {
            throw new Error(`The bitcash wallet document holds two wallets in slot ${wallet.slot}`);
        }
        seen.add(wallet.slot);
        return wallet;
    });
    return { v: 1, wallets };
}

function validateRecord(raw: unknown): BitcashWalletRecord {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
        throw new Error('A bitcash wallet record is unreadable');
    }
    const o = raw as Record<string, unknown>;

    const slot = o.slot;
    if (typeof slot !== 'number' || !Number.isInteger(slot) || slot < 0) {
        throw new Error(`A bitcash wallet record has an invalid slot: ${String(slot)}`);
    }
    // A FUND address and an AUTH token both fail this deliberately: the
    // document holds the SPENDING key, and only that.
    if (typeof o.key !== 'string' || !verifyWalletKey(o.key)) {
        throw new Error(`The wallet in slot ${slot} does not hold a valid wallet key`);
    }
    const key = toCompact(o.key);
    if (extractType(key) !== TYPE_CASH) {
        throw new Error(`The wallet in slot ${slot} is not a CASH spending wallet key`);
    }
    if (typeof o.label !== 'string' || o.label.length > BITCASH_WALLET_LABEL_MAX) {
        throw new Error(`A wallet label is at most ${BITCASH_WALLET_LABEL_MAX} characters`);
    }
    if (typeof o.createdAt !== 'number' || !Number.isInteger(o.createdAt) || o.createdAt < 0) {
        throw new Error(`The wallet in slot ${slot} has an invalid creation time`);
    }
    if (!SOURCES.includes(o.source as BitcashWalletSource)) {
        throw new Error(`Unknown bitcash wallet source: ${String(o.source)}`);
    }
    if (!STATUSES.includes(o.status as BitcashWalletStatus)) {
        throw new Error(`Unknown bitcash wallet status: ${String(o.status)}`);
    }
    return {
        slot,
        key,
        label: o.label,
        createdAt: o.createdAt,
        source: o.source as BitcashWalletSource,
        status: o.status as BitcashWalletStatus,
    };
}

/** The slot the NEXT wallet takes: one past the highest present, REVOKED
 *  wallets included. See the module header for why a slot is never reused. */
export function nextSlot(doc: BitcashWalletsDocument): number {
    return doc.wallets.length === 0
        ? 0
        : Math.max(...doc.wallets.map(w => w.slot)) + 1;
}

/** Every ACTIVE wallet, in slot order. A member may hold many at once (D3). */
export function activeWallets(doc: BitcashWalletsDocument): BitcashWalletRecord[] {
    return doc.wallets
        .filter(w => w.status === 'active')
        .sort((a, b) => a.slot - b.slot);
}

/** The ACTIVE wallets as the panes and the consent picker render them. */
export function walletSlotsFromDocument(doc: BitcashWalletsDocument): WalletSlot[] {
    return activeWallets(doc).map(w => ({
        keyIndex: w.slot,
        key: w.key,
        label: w.label,
        source: w.source,
        createdAt: w.createdAt,
    }));
}

/** Default label for a wallet the member never named. */
export function walletDisplayLabel(slot: WalletSlot): string {
    if (slot.label.trim()) return slot.label;
    return slot.keyIndex === 0 ? 'Registration wallet' : `Wallet ${slot.keyIndex}`;
}

// ── writing (pure planners; the store lands them) ───────────────────────────

export function requireWalletLabel(label: string): string {
    const trimmed = label.trim();
    if (trimmed.length > BITCASH_WALLET_LABEL_MAX) {
        throw new Error(`A wallet label is at most ${BITCASH_WALLET_LABEL_MAX} characters`);
    }
    return trimmed;
}

export interface ImportWalletInput {
    /** What the member pasted, in either form. */
    code: string;
    /** The wallets the document already holds, REVOKED ones included: a
     *  re-import would leave two records for one key, and the revoked one would
     *  keep hiding it from the manager. */
    existing: readonly BitcashWalletRecord[];
    label?: string;
    /** Unix SECONDS. Defaults to the clock. */
    now?: () => number;
}

/**
 * *Add wallet…* — a pasted CASH key becomes a record (D2's "import any wallet
 * later"). Two refusals, each of them a way to lose money rather than a typo:
 *   * anything that is not a valid key — a mistyped code names no wallet;
 *   * a key that is not CASH — an AUTH token is an authorisation, a FUND
 *     address is public, and a GIFT/DEMO wallet is not one the member owns.
 * Any valid CASH code is accepted, whatever its second group: that group is
 * body, not an issuing operator (docs/cid-governance.md M13).
 */
export function planWalletImport(input: ImportWalletInput): NewWalletRecord {
    const code = input.code.trim();
    if (!code || !verifyWalletKey(code)) {
        throw new Error('That is not a valid bitcash wallet key — check the code and try again');
    }
    const compact = toCompact(code);
    if (extractType(compact) !== TYPE_CASH) {
        throw new Error('Only a CASH spending wallet key can be added here. '
            + 'A receive address, an AUTH token and a gift or demo wallet are not account wallets.');
    }
    if (input.existing.some(w => w.key === compact)) {
        throw new Error('That wallet is already in your wallet list');
    }
    return {
        key: compact,
        label: requireWalletLabel(input.label ?? ''),
        createdAt: nowSeconds(input.now),
        source: 'import',
        status: 'active',
    };
}

export interface NewWalletInput {
    label?: string;
    /** One byte per call. Defaults to the platform CSPRNG; tests pin it. */
    randomByte?: () => number;
    /** Unix SECONDS. Defaults to the clock. */
    now?: () => number;
}

/**
 * *New wallet…* — mint a CASH key in the v2 layout through the same rejection
 * sampler every other key in this lane uses (§4.9), from CSPRNG bytes. Nothing
 * here touches the ledger: a v2 wallet exists only from its first credit of at
 * least 1000 BIT — a gift redeemed into it or a lock released into it
 * (apps/bitcash/docs/plans/bitcash-v2.md M12) — so until then it has no balance and no address.
 */
export function planNewWallet(input: NewWalletInput): NewWalletRecord {
    const nextByte = input.randomByte ?? csprngByte;
    return {
        key: generateWalletKeyFrom(nextByte, TYPE_CASH),
        label: requireWalletLabel(input.label ?? ''),
        createdAt: nowSeconds(input.now),
        source: 'settings',
        status: 'active',
    };
}

/** File a new record at the next free slot. Pure: returns a new document. */
export function appendWallet(
    doc: BitcashWalletsDocument, record: NewWalletRecord,
): { doc: BitcashWalletsDocument; slot: number } {
    const slot = nextSlot(doc);
    return {
        doc: { v: 1, wallets: [...doc.wallets, { ...record, slot }] },
        slot,
    };
}

/**
 * Rename one wallet. Everything but the label is carried across unchanged —
 * the key, the creation time, where the wallet came from and its status. Pure:
 * returns a new document.
 */
export function planWalletRename(
    doc: BitcashWalletsDocument, slot: number, label: string,
): BitcashWalletsDocument {
    const next = requireWalletLabel(label);
    requireSlot(doc, slot);
    return {
        v: 1,
        wallets: doc.wallets.map(w => (w.slot === slot ? { ...w, label: next } : w)),
    };
}

/**
 * Retire one wallet. The record STAYS — see the module header: the slot is
 * never reused, and the bitcash manager learns to stop offering the wallet from
 * the `revoked` status, which it could not learn from an absence.
 */
export function revokeWallet(
    doc: BitcashWalletsDocument, slot: number,
): BitcashWalletsDocument {
    requireSlot(doc, slot);
    return {
        v: 1,
        wallets: doc.wallets.map(w => (w.slot === slot ? { ...w, status: 'revoked' } : w)),
    };
}

function requireSlot(doc: BitcashWalletsDocument, slot: number): BitcashWalletRecord {
    const found = doc.wallets.find(w => w.slot === slot);
    if (!found) throw new Error(`Your wallet list holds no wallet in slot ${slot}`);
    return found;
}

function csprngByte(): number {
    const buffer = new Uint8Array(1);
    crypto.getRandomValues(buffer);
    return buffer[0];
}

/** Unix SECONDS, the unit the document stores. */
function nowSeconds(now?: () => number): number {
    return now ? now() : Math.floor(Date.now() / 1000);
}
